// JSON-LD structured-data builders for MODULAROOM.
// All schemas follow schema.org — validated for Google's rich-result guidelines.

const BASE = 'https://modularoom.com';

// Verified against the official Google Business listing at https://maps.app.goo.gl/AFxfe7kTu4UXc5hQ8
// (Google Place ID 0x3959cb35a710c389:0x6ad86be61eab2f98)
const OFFICE_ADDRESS = {
  '@type': 'PostalAddress',
  streetAddress: '104, Jay Jaliyan Complex – B, Karan Park Street 1, Main Road, near Astron Society, Vidhut Nagar',
  addressLocality: 'Rajkot',
  addressRegion: 'Gujarat',
  postalCode: '360001',
  addressCountry: 'IN',
};
const OFFICE_GEO = { '@type': 'GeoCoordinates', latitude: 22.2818806, longitude: 70.7796964 };
const OFFICE_MAP_URL = 'https://maps.app.goo.gl/AFxfe7kTu4UXc5hQ8';

// ---------- Organization (site-wide) ----------
export const organizationSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'Organization',
  '@id': `${BASE}/#organization`,
  name: 'MODULAROOM',
  legalName: 'MODULAROOM',
  url: BASE,
  logo: `${BASE}/logo.png`,
  description:
    'MODULAROOM designs and manufactures luxury modular homes, capsule suites and prefab cottages. Factory-built modular resort rooms, glamping cabins and modular offices, delivered worldwide from India.',
  foundingLocation: 'Rajkot, Gujarat, India',
  areaServed: { '@type': 'Place', name: 'Worldwide' },
  address: OFFICE_ADDRESS,
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '+91-98750-67172',
    contactType: 'sales',
    email: 'modularoom.info@gmail.com',
    availableLanguage: ['English', 'Hindi', 'Gujarati'],
  },
  sameAs: [
    'https://instagram.com/modularoom',
    'https://linkedin.com/company/modularoom',
    'https://facebook.com/modularoomindia',
    'https://www.youtube.com/@modularoom',
  ],
});

// ---------- WebSite (with sitelinks search box) ----------
export const websiteSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  '@id': `${BASE}/#website`,
  url: BASE,
  name: 'MODULAROOM',
  publisher: { '@id': `${BASE}/#organization` },
});

// ---------- LocalBusiness (Contact page) ----------
export const localBusinessSchema = () => ({
  '@context': 'https://schema.org',
  '@type': 'LocalBusiness',
  '@id': `${BASE}/contact#localbusiness`,
  name: 'MODULAROOM',
  image: `${BASE}/og-image.jpg`,
  url: BASE,
  hasMap: OFFICE_MAP_URL,
  telephone: '+91-98750-67172',
  email: 'modularoom.info@gmail.com',
  priceRange: '₹₹₹₹',
  address: OFFICE_ADDRESS,
  geo: OFFICE_GEO,
  openingHoursSpecification: [
    {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'],
      opens: '09:30',
      closes: '19:00',
    },
  ],
});

// ---------- Product (per model page) ----------
export const productSchema = (product, familyName, basePriceInr) => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  '@id': `${BASE}/products/${product.slug}#product`,
  name: product.name,
  description: product.overview,
  image: product.hero,
  brand: { '@type': 'Brand', name: 'MODULAROOM' },
  manufacturer: { '@id': `${BASE}/#organization` },
  category: `Luxury Modular Homes · ${familyName}`,
  sku: product.name,
  additionalProperty: [
    { '@type': 'PropertyValue', name: 'Length', value: product.specs.length },
    { '@type': 'PropertyValue', name: 'Width', value: product.specs.width },
    { '@type': 'PropertyValue', name: 'Height', value: product.specs.height },
    { '@type': 'PropertyValue', name: 'Carpet Area', value: product.specs.carpetArea },
    { '@type': 'PropertyValue', name: 'Family', value: familyName },
  ],
  ...(basePriceInr && {
    offers: {
      '@type': 'Offer',
      price: String(basePriceInr),
      priceCurrency: 'INR',
      availability: 'https://schema.org/InStock',
      priceValidUntil: '2026-12-31',
      seller: { '@id': `${BASE}/#organization` },
      url: `${BASE}/products/${product.slug}`,
    },
  }),
});

// ---------- BreadcrumbList ----------
export const breadcrumbSchema = (items) => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: items.map((it, i) => ({
    '@type': 'ListItem',
    position: i + 1,
    name: it.name,
    ...(it.url && { item: it.url.startsWith('http') ? it.url : `${BASE}${it.url}` }),
  })),
});

// ---------- FAQPage ----------
export const faqSchema = (faqs) => ({
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: faqs.map((f) => ({
    '@type': 'Question',
    name: f.q,
    acceptedAnswer: { '@type': 'Answer', text: f.a },
  })),
});
